import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Alert,
  Skeleton,
  ErrorBoundary,
  GstinInput,
  Input,
  useToast,
} from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { subscriptionService } from '@/services/subscription.service';
import { tenantService } from '@/services/tenant.service';
import { InvoiceHistoryPanel } from '@/components/features/subscriptions/InvoiceHistoryPanel';
import { ChangeHistoryTimeline } from '@/components/features/subscriptions/ChangeHistoryTimeline';
import { PlanCard } from '@/components/features/subscriptions/PlanCard';
import { UsageSection } from '@/components/features/subscriptions/UsageSection';
import { PlanFeaturesSection } from '@/components/features/subscriptions/PlanFeaturesSection';
import { GLASS_PANEL, cn, formatINRDate } from '@/components/features/subscriptions/design-tokens';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTaxRegime } from '@/hooks/use-tax-regime';

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function HeroSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      <Skeleton variant="rect" height={220} className="rounded-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={320} className="rounded-2xl" />
        ))}
      </div>
      <Skeleton variant="rect" height={280} className="rounded-2xl" />
    </div>
  );
}

// Hero gradient classes chosen by subscription status so the page reads
// different on sight between healthy / warning / danger states.
function heroGradient(status: string): string {
  if (status === 'SUSPENDED') {
    return 'from-rose-600 via-rose-500 to-orange-500 shadow-[0_20px_60px_-20px_rgba(225,29,72,0.55)]';
  }
  if (status === 'TRIAL') {
    return 'from-sky-600 via-indigo-600 to-violet-600 shadow-[0_20px_60px_-20px_rgba(79,70,229,0.55)]';
  }
  if (status === 'ACTIVE') {
    return 'from-emerald-600 via-teal-600 to-sky-600 shadow-[0_20px_60px_-20px_rgba(5,150,105,0.55)]';
  }
  return 'from-neutral-700 to-neutral-800 shadow-[0_20px_60px_-20px_rgba(23,23,23,0.45)]';
}

/** A seat limit for the hero: "—" while loading, "Unlimited" for null. */
function seatLimit(value: number | null | undefined): string | number {
  if (value === undefined) return '—';
  return value ?? 'Unlimited';
}

function MyContent() {
  const profile = useAuthStore((s) => s.profile);
  const tenantId = profile?.tenant_id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tax = useTaxRegime();

  const [gstin, setGstin] = useState('');
  const [gstinTouched, setGstinTouched] = useState(false);
  const [gstinValid, setGstinValid] = useState(true);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');

  const { data: details, isLoading, error } = useQuery({
    queryKey: ['subscription', 'details', tenantId],
    queryFn: () => subscriptionService.getDetails(tenantId!),
    enabled: Boolean(tenantId),
  });

  const { data: tenant } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => tenantService.getById(tenantId!),
    enabled: Boolean(tenantId),
  });

  // Effective limits and features — plan values with this business's overrides.
  const { data: limits } = useQuery({
    queryKey: ['tenant-limits', tenantId],
    queryFn: () => tenantService.getLimits(tenantId!),
    enabled: Boolean(tenantId),
  });

  const { data: usage } = useQuery({
    queryKey: ['tenant-usage', tenantId],
    queryFn: () => tenantService.getUsage(tenantId!),
    enabled: Boolean(tenantId),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['subscription-plans', 'active'],
    queryFn: () => subscriptionService.listPlans(false),
    staleTime: 60_000,
  });

  // Seed the billing form once the tenant payload arrives.
  useEffect(() => {
    if (!tenant) return;
    setGstin(tenant.gstin ?? '');
    setAddress(tenant.address ?? '');
    setCity(tenant.city ?? '');
    setState(tenant.state ?? '');
    setPincode(tenant.pincode ?? '');
  }, [tenant]);

  const billingMutation = useMutation({
    mutationFn: () =>
      tenantService.updateProfile(tenantId!, {
        gstin: gstin.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        pincode: pincode.trim() || undefined,
      }),
    onSuccess: () => {
      toast({ title: 'Billing details updated', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const activePlans = useMemo(
    () => [...plans].filter((p) => p.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [plans],
  );

  if (!tenantId) {
    return (
      <Alert
        variant="danger"
        title="No tenant context"
        description="Your account is not linked to a tenant. Contact platform support."
      />
    );
  }

  if (isLoading) return <HeroSkeleton />;

  if (error || !details) {
    return (
      <Alert
        variant="danger"
        title="Failed to load subscription"
        description={(error as Error)?.message ?? 'Try refreshing the page.'}
      />
    );
  }

  const status = details.status;
  const daysRemaining = details.days_remaining ?? 0;
  const hasPendingDowngrade = Boolean(details.pending_downgrade);
  const urgent = status === 'TRIAL' && daysRemaining <= 3;
  const gradient = heroGradient(status);

  return (
    <>
      {/* Hero — rich gradient with status-aware tone */}
      <div className={cn('relative mb-6 overflow-hidden rounded-2xl p-8 text-white animate-fade-in bg-gradient-to-br', gradient)}>
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-black/15 blur-3xl" />

        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ring-1 ring-inset ring-white/20">
              <span className="relative inline-flex h-2 w-2">
                {urgent && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 opacity-75" />
                )}
                <span className={cn('relative inline-flex h-2 w-2 rounded-full', urgent ? 'bg-white' : 'bg-white/90')} />
              </span>
              {status} SUBSCRIPTION
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {details.current_plan.display_name}
            </h1>
            <p className="mt-1 text-white/80">
              {tenant?.business_name ?? profile?.tenant?.business_name ?? 'Your business'} · Orio360 platform
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {details.is_read_only && <Badge variant="danger">Read-only grace period</Badge>}
              {hasPendingDowngrade && (
                <Badge variant="warning">
                  Downgrade to {details.pending_downgrade!.new_plan} pending
                </Badge>
              )}
              {!details.is_read_only && !hasPendingDowngrade && (
                <span className="text-xs text-white/75">Everything is up to date</span>
              )}
            </div>
          </div>
          <div className="text-right">
            {details.current_plan.price_monthly != null && (
              <>
                <p className="text-4xl font-bold tabular-nums">
                  {formatCurrency(
                    details.current_plan.price_monthly,
                    toCurrencyCode(details.current_plan.currency),
                  )}
                </p>
                <p className="mt-1 text-xs text-white/70">per month · billed separately</p>
              </>
            )}
            <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white/15 px-3 py-1.5 text-xs font-medium ring-1 ring-inset ring-white/20">
              <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              <span className="tabular-nums">{daysRemaining} days remaining</span>
            </div>
          </div>
        </div>

        <div className="relative mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <HeroStat label="Started" value={formatINRDate(details.subscription_started_at)} />
          <HeroStat label="Expires" value={formatINRDate(details.subscription_expires_at)} />
          <HeroStat label="Locations included" value={seatLimit(limits?.max_locations)} />
          <HeroStat label="Users included" value={seatLimit(limits?.max_users)} />
        </div>
      </div>

      {details.is_read_only && (
        <div className="mb-6">
          <Alert
            variant="danger"
            title="Your account is in read-only grace period"
            description="Renew or upgrade your subscription to restore full access before the grace period ends."
            action={{ label: 'Contact support', onClick: () => navigate('/communications') }}
          />
        </div>
      )}

      {usage && <UsageSection usage={usage} />}

      {limits && <PlanFeaturesSection features={limits.features} />}

      {/* Compare plans */}
      {activePlans.length > 0 && (
        <div className="mb-6 animate-slide-up">
          <div className="mb-4 flex items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                Compare available plans
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Request a plan change and our platform team will handle prorated billing.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {activePlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingCycle="yearly"
                current={plan.name === details.current_plan.name}
                selectLabel={
                  plan.name === details.current_plan.name
                    ? 'Your current plan'
                    : 'Request this plan'
                }
                onSelect={(p) => {
                  if (p.name === details.current_plan.name) return;
                  navigate(
                    `/communications?subject=${encodeURIComponent(
                      `Plan change request: ${details.current_plan.display_name} → ${p.display_name}`,
                    )}`,
                  );
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <InvoiceHistoryPanel tenantId={tenantId} heading="Your invoices" />
        <ChangeHistoryTimeline tenantId={tenantId} heading="Plan history" />
      </div>

      {/* Billing details */}
      <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white shadow-[0_8px_20px_-8px_rgba(79,70,229,0.55)]">
            <Icon d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              Billing details
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Shown on every subscription invoice. Update here once — it flows into all future receipts.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-1">
            <GstinInput
              regime={tax}
              label={tax.taxIdLabel ? `Business ${tax.taxIdLabel}` : undefined}
              value={gstin}
              onChange={(v) => {
                setGstin(v);
                setGstinTouched(true);
              }}
              onValidated={setGstinValid}
              error={
                gstinTouched && gstin && !gstinValid
                  ? `Enter a valid ${tax.taxIdLabel ?? 'tax ID'}`
                  : undefined
              }
            />
          </div>
          <div className="md:col-span-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              City
            </label>
            <Input value={city} onChange={setCity} placeholder="Bengaluru" />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Billing address
            </label>
            <Input
              value={address}
              onChange={setAddress}
              placeholder="Shop 12, Indiranagar 100 ft Road"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              State
            </label>
            <Input value={state} onChange={setState} placeholder="Karnataka" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              PIN code
            </label>
            <Input value={pincode} onChange={setPincode} placeholder="560038" />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            variant="primary"
            onClick={() => billingMutation.mutate()}
            loading={billingMutation.isPending}
            disabled={Boolean(gstin) && !gstinValid}
          >
            Save billing details
          </Button>
        </div>
      </div>
    </>
  );
}

function HeroStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-white/12 p-3 ring-1 ring-inset ring-white/15 backdrop-blur-sm">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">{label}</p>
      <p className="mt-1 text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

export function MySubscriptionPage() {
  return (
    <ErrorBoundary>
      <PageContainer variant="default">
        <MyContent />
      </PageContainer>
    </ErrorBoundary>
  );
}
